import { Link } from '@carbon/icons-react';
import * as React from 'react';
import {
  Accordion,
  Body,
  Button,
  Input,
  Loading,
  SystemMessage,
} from 'vet-bones/design-system/components';
import { isEqual } from 'vet-bones/utils';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { FormField } from 'src/components/dataSourceSelection/FormField';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import { BaseFooterOptions } from 'src/components/resourceDialog/DialogFooter';
import {
  DataSourceOption,
  DataSourceType,
  JDBC_DRIVER_KEY,
  STANDARD_JDBC_PROPERTY_KEYS,
  TypeDescriptionCombinedOption,
} from 'src/constants/dataSourceTypeDescription';
import {
  TEST_IDS,
  getDataSourceStandardOptionTestId,
} from 'src/constants/testIds';
import { AddDataSourcePanel } from 'src/containers/ProjectNewResourceDialogV2/panels/AddDataSourcePanel';
import { useValidateDataSourceName } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import {
  AdvancedOption,
  AdvancedOptions,
} from 'src/containers/ProjectVirtualGraphPreviewDialogV2/components/AdvancedOptionsV2';
import { useProjectVirtualGraphPreviewDialog } from 'src/containers/ProjectVirtualGraphPreviewDialogV2/context/useProjectVirtualGraphPreviewDialog';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { dataSourcePropertiesSelector } from 'src/selectors/dataSourceProperties';
import { SqlDataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import {
  countUniqueExistingProperties,
  isRowInvalid,
} from 'src/utils/genericPropertyValidation';
import { useDialogPanelSetup } from 'src/utils/hooks/useDialogPanelSetup';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

import styles from 'src/containers/ProjectVirtualGraphPreviewDialogV2/components/dataSourceCards.module.scss';

export type DataSourceCreationPanelProps = {
  dataSourceType: DataSourceType;
  contextOverrides?: {
    setDialogTitle: (title: string) => void;
    setHeaderBackButton: (onBack: (() => void) | null) => void;
    setFullSize: (fullSize: boolean) => void;
    setFooterOptions: (options: BaseFooterOptions) => void;
  };
};

export const DataSourceCreationPanel: React.VFC<
  PanelProps<DataSourceCreationPanelProps>
> = ({
  closePanel,
  closeToRoot,
  openPanel,
  dataSourceType,
  contextOverrides,
}) => {
  const dispatch = useReduxDispatch();
  const vgContext = useProjectVirtualGraphPreviewDialog();

  const setDialogTitle =
    contextOverrides?.setDialogTitle ?? vgContext.setDialogTitle;
  const setHeaderBackButton =
    contextOverrides?.setHeaderBackButton ?? vgContext.setHeaderBackButton;
  const setFullSize = contextOverrides?.setFullSize ?? vgContext.setFullSize;
  const setFooterOptions =
    contextOverrides?.setFooterOptions ?? vgContext.setFooterOptions;

  const { localVirtualGraph, updateVirtualGraphDataSource, setErrorText } =
    vgContext;

  const connection = useReduxSelector(connectionSelector);
  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const propertyDescriptions = useReduxSelector(
    dataSourcePropertiesSelector(connection, dataSourceType),
    isEqual
  );
  const isPendingDataSourceCreation = useReduxSelector(
    (state) => state.dataSources.pendingAddDataSource
  );

  const [dataSourceName, setDataSourceName] = React.useState('');
  const [standardProperties, setStandardProperties] = React.useState<
    Record<string, string>
  >(() =>
    Array.from(STANDARD_JDBC_PROPERTY_KEYS).reduce(
      (agg, property) => ({
        ...agg,
        [property]: propertyDescriptions[property]?.default || '',
      }),
      {}
    )
  );
  const [advancedOptions, setAdvancedOptions] = React.useState<
    AdvancedOption[]
  >([{ property: '', value: '' }]);
  const [isAdvancedOpen, setIsAdvancedOpen] = React.useState(false);
  const [touchedFields, setTouchedFields] = React.useState<{
    dataSourceName: boolean;
    endpoint: boolean;
  }>({ dataSourceName: false, endpoint: false });

  const validateName = useValidateDataSourceName();
  const dataSourceNameError = validateName(dataSourceName);

  const dataSourceLabel =
    dataSourceLabelsByType[dataSourceType] || dataSourceType;

  const {
    invalidDataSourcePropertyNames,
    missingDataSourcePropertyNames,
    newDataSourceOptions,
  } = React.useMemo(() => {
    const invalidDataSourcePropertyNames = new Set<string>();
    const missingDataSourcePropertyNames = new Set<string>();
    const newDataSourceOptions: Record<string, string | undefined> = {
      [JDBC_DRIVER_KEY]:
        propertyDescriptions?.[JDBC_DRIVER_KEY]?.default !== undefined
          ? String(propertyDescriptions[JDBC_DRIVER_KEY].default)
          : undefined,
    };

    Object.entries(DataSourceOption).forEach(([propertyNameArg, property]) => {
      const propertyName = propertyNameArg as keyof typeof DataSourceOption;
      newDataSourceOptions[property] = standardProperties[property];
      if (
        propertyDescriptions[property]?.required &&
        !newDataSourceOptions[property]
      ) {
        missingDataSourcePropertyNames.add(
          copy.components.projectResourceDialog.projectDataSourceCreationPanel
            .propertyLabels[propertyName]
        );
      }
    });

    const existingPropertyCounts = countUniqueExistingProperties(
      advancedOptions.map((opt, idx) => ({
        key: `${opt.property}-${idx}`,
        name: opt.property,
        value: opt.value,
      }))
    );
    advancedOptions.forEach(({ property, value }) => {
      if (property.trim()) {
        newDataSourceOptions[property] = value;
        if (
          isRowInvalid(
            property,
            value,
            STANDARD_JDBC_PROPERTY_KEYS,
            existingPropertyCounts
          )
        ) {
          invalidDataSourcePropertyNames.add(property);
        }
      }
    });

    return {
      invalidDataSourcePropertyNames,
      missingDataSourcePropertyNames,
      newDataSourceOptions,
    };
  }, [propertyDescriptions, advancedOptions, standardProperties]);

  const advancedPropertySuggestions = React.useMemo(() => {
    return Object.keys(propertyDescriptions)
      .filter(
        (property) =>
          !(property in DataSourceOption) && property !== JDBC_DRIVER_KEY
      )
      .sort();
  }, [propertyDescriptions]);

  const advancedPropertyMetadata = React.useMemo(() => {
    const result: Record<string, TypeDescriptionCombinedOption> = {};
    Object.entries(propertyDescriptions).forEach(([property, metadata]) => {
      if (
        !(property in DataSourceOption) &&
        property !== JDBC_DRIVER_KEY &&
        metadata !== undefined
      ) {
        result[property] = metadata;
      }
    });
    return result;
  }, [propertyDescriptions]);

  const getValidationError = React.useCallback(() => {
    if (dataSourceNameError) {
      return dataSourceNameError;
    }
    if (invalidDataSourcePropertyNames.size > 0) {
      return copy.components.projectResourceDialog.projectDataSourceCreationPanel.invalidDataSourceProperties(
        Array.from(invalidDataSourcePropertyNames.values())
      );
    }
    if (missingDataSourcePropertyNames.size > 0) {
      return copy.components.projectResourceDialog.projectDataSourceCreationPanel.missingDataSourceProperties(
        Array.from(missingDataSourcePropertyNames.values())
      );
    }
    return null;
  }, [
    dataSourceNameError,
    invalidDataSourcePropertyNames,
    missingDataSourcePropertyNames,
  ]);

  const getDisplayValidationError = React.useCallback(() => {
    if (touchedFields.dataSourceName && dataSourceNameError) {
      return dataSourceNameError;
    }
    if (invalidDataSourcePropertyNames.size > 0) {
      return copy.components.projectResourceDialog.projectDataSourceCreationPanel.invalidDataSourceProperties(
        Array.from(invalidDataSourcePropertyNames.values())
      );
    }
    if (touchedFields.endpoint && missingDataSourcePropertyNames.size > 0) {
      return copy.components.projectResourceDialog.projectDataSourceCreationPanel.missingDataSourceProperties(
        Array.from(missingDataSourcePropertyNames.values())
      );
    }
    return null;
  }, [
    touchedFields,
    dataSourceNameError,
    invalidDataSourcePropertyNames,
    missingDataSourcePropertyNames,
  ]);

  const handleBack = React.useCallback(() => {
    closePanel?.();
  }, [closePanel]);

  const handleConnect = React.useCallback(async () => {
    const validationError = getValidationError();
    if (validationError || !connection) {
      if (validationError) {
        setErrorText(validationError);
      }
      return;
    }

    const newDataSource: SqlDataSource = {
      entityName: '',
      available: true,
      sharable: true,
      id: dataSourceName,
      type: dataSourceType,
      options: newDataSourceOptions as SqlDataSource['options'],
      tables: [],
      tableMetadataErrorResponse: null,
    };

    const didSucceed = await dispatch(addDataSource(newDataSource));

    if (!didSucceed) {
      return;
    }

    // If we have a local virtual graph (replace flow), update it and close to root
    if (localVirtualGraph) {
      updateVirtualGraphDataSource({
        connectionId: connection.id || null,
        dataSourceEndpoint: newDataSource.options['jdbc.url'],
        dataSourceId: dataSourceName,
        dataSourceType,
      });
      setErrorText('');
      closeToRoot?.();
    } else {
      // Add flow - refresh tables and open AddDataSourcePanel with the newly created data source
      setErrorText('');

      // Refresh tables for the newly created data source
      await dispatch(refreshDataSourceTables(dataSourceName));

      // Close all panels back to root, then open AddDataSourcePanel
      closeToRoot?.();
      openPanel?.({
        props: {
          dataSourceType,
          dataSourceId: dataSourceName,
          skipToTables: true,
        },
        renderPanel: AddDataSourcePanel,
      });
    }
  }, [
    closeToRoot,
    connection,
    dataSourceName,
    dataSourceType,
    dispatch,
    getValidationError,
    newDataSourceOptions,
    openPanel,
    setErrorText,
    updateVirtualGraphDataSource,
    localVirtualGraph,
  ]);

  const handleStandardPropertyChange = React.useCallback(
    (property: string, value: string) => {
      setStandardProperties((prev) => ({
        ...prev,
        [property]: value,
      }));
    },
    []
  );

  const handleFieldBlur = React.useCallback(
    (field: 'dataSourceName' | 'endpoint') => {
      setTouchedFields((prev) => ({
        ...prev,
        [field]: true,
      }));
    },
    []
  );

  useDialogPanelSetup(
    { setDialogTitle, setHeaderBackButton, setFullSize, setFooterOptions },
    {
      title:
        copy.components.virtualGraphPreviewDialog.dataSourceCreation.title(
          dataSourceLabel
        ),
      fullSize: false,
      onBack: handleBack,
    }
  );

  const validFilledAdvancedOptions = advancedOptions.filter(
    (opt) => opt.property.trim() !== ''
  ).length;

  if (isPendingDataSourceCreation) {
    return (
      <div className={styles.creationFormContainer}>
        <div className={styles.panelLoading}>
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.creationFormContainer}>
      <div className={styles.creationFormContent}>
        <div className={styles.creationFormSection}>
          <Body size="md" weight="semibold">
            {
              copy.components.virtualGraphPreviewDialog.dataSourceCreation
                .connectionCredentials
            }
          </Body>

          <div className={styles.creationFormFields}>
            <FormField
              id="connection-name"
              label={
                copy.components.virtualGraphPreviewDialog.dataSourceCreation
                  .connectionName
              }
              required
            >
              <Input
                clearable={false}
                id="connection-name"
                invalid={
                  touchedFields.dataSourceName && Boolean(dataSourceNameError)
                }
                label=""
                onBlur={() => handleFieldBlur('dataSourceName')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDataSourceName(e.target.value)
                }
                size="md"
                testId={getDataSourceStandardOptionTestId('DATA_SOURCE_NAME')}
                value={dataSourceName}
              />
            </FormField>

            <FormField
              id="username"
              label={
                copy.components.virtualGraphPreviewDialog.dataSourceCreation
                  .username
              }
            >
              <Input
                clearable={false}
                id="username"
                label=""
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleStandardPropertyChange(
                    DataSourceOption.USERNAME,
                    e.target.value
                  )
                }
                size="md"
                testId={getDataSourceStandardOptionTestId(
                  DataSourceOption.USERNAME
                )}
                value={standardProperties[DataSourceOption.USERNAME] || ''}
              />
            </FormField>

            <FormField
              id="endpoint"
              label={
                copy.components.virtualGraphPreviewDialog.dataSourceCreation
                  .endpoint
              }
              required
            >
              <Input
                clearable={false}
                id="endpoint"
                invalid={
                  touchedFields.endpoint &&
                  missingDataSourcePropertyNames.has(
                    copy.components.projectResourceDialog
                      .projectDataSourceCreationPanel.propertyLabels.ENDPOINT
                  )
                }
                label=""
                onBlur={() => handleFieldBlur('endpoint')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleStandardPropertyChange(
                    DataSourceOption.ENDPOINT,
                    e.target.value
                  )
                }
                size="md"
                testId={getDataSourceStandardOptionTestId(
                  DataSourceOption.ENDPOINT
                )}
                value={standardProperties[DataSourceOption.ENDPOINT] || ''}
              />
            </FormField>

            <FormField
              id="password"
              label={
                copy.components.virtualGraphPreviewDialog.dataSourceCreation
                  .password
              }
            >
              <Input
                clearable={false}
                id="password"
                label=""
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleStandardPropertyChange(
                    DataSourceOption.PASSWORD,
                    e.target.value
                  )
                }
                size="md"
                testId={getDataSourceStandardOptionTestId(
                  DataSourceOption.PASSWORD
                )}
                type="password"
                value={standardProperties[DataSourceOption.PASSWORD] || ''}
              />
            </FormField>
          </div>
        </div>

        {/* Advanced Options */}
        <Accordion
          icon="left"
          isOpen={isAdvancedOpen}
          label={copy.components.virtualGraphPreviewDialog.advancedOptions.title(
            validFilledAdvancedOptions
          )}
          onInteraction={setIsAdvancedOpen}
          size="md"
        >
          <AdvancedOptions
            onOptionsChange={setAdvancedOptions}
            options={advancedOptions}
            propertyMetadata={advancedPropertyMetadata}
            propertySuggestions={advancedPropertySuggestions}
          />
        </Accordion>
      </div>

      <div className={styles.creationFormFooter}>
        {getDisplayValidationError() ? (
          <SystemMessage
            text={getDisplayValidationError() || ''}
            variant="alert"
          />
        ) : null}
        <Button
          disabled={
            isPendingDataSourceCreation || Boolean(getValidationError())
          }
          onClick={handleConnect}
          rightIcon={
            isPendingDataSourceCreation ? () => <Loading size="sm" /> : Link
          }
          size="md"
          testId={TEST_IDS.projectResourceDialog.createButton}
          variant="primary-accent"
        >
          {isPendingDataSourceCreation
            ? copy.components.virtualGraphPreviewDialog.dataSourceCreation
                .connecting
            : copy.components.virtualGraphPreviewDialog.dataSourceCreation
                .connect}
        </Button>
      </div>
    </div>
  );
};
